import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TrendingUp, Calendar, AlertCircle, ChevronLeft, ChevronRight } from 'lucide-react';
import { API_URL } from '../../../config/api';
import { useAuth } from '../../../context/AuthContext';
import { useProjects } from '../../../context/ProjectContext';

function CurrentMonthWidget() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const { selectedProject } = useProjects();

  const [isLoading, setIsLoading] = useState(false);
  const [, setError] = useState('');
  const [monthLabel, setMonthLabel] = useState('');
  const [totalTons, setTotalTons] = useState(0);
  const [changePct, setChangePct] = useState(0);
  const [isIncrease, setIsIncrease] = useState(false);
  const [targetTons, setTargetTons] = useState(0);
  const [dailyAvgTons, setDailyAvgTons] = useState(0);
  const [projectedTons, setProjectedTons] = useState(0);
  const [topSources, setTopSources] = useState([]);

  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [selectedMonthISO, setSelectedMonthISO] = useState(() => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    return `${y}-${m}`;
  });
  const [pickerYear, setPickerYear] = useState(() => new Date().getFullYear());
  const pickerRef = useRef(null);

  const currentYear = useMemo(() => {
    try {
      const [y] = String(selectedMonthISO || '').split('-');
      return Number(y) || new Date().getFullYear();
    } catch {
      return new Date().getFullYear();
    }
  }, [selectedMonthISO]);

  const displayMonthLabel = useMemo(() => {
    try {
      const [y, mm] = String(selectedMonthISO || '').split('-');
      const d = new Date(Number(y), Number(mm) - 1, 1);
      const locale = i18n?.language || 'en-US';
      return d.toLocaleString(locale, { month: 'long' });
    } catch {
      return monthLabel || new Date().toLocaleString('en-US', { month: 'long' });
    }
  }, [selectedMonthISO, monthLabel, i18n?.language]);

  useEffect(() => {
    if (!isPickerOpen) return;
    const onDocClick = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) {
        setIsPickerOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [isPickerOpen]);

  useEffect(() => {
    const projectId = selectedProject?.id;
    if (!user?.token || !projectId) {
      setError('');
      setIsLoading(false);
      setMonthLabel('');
      setTotalTons(0);
      setChangePct(0);
      setIsIncrease(false);
      setTargetTons(0);
      setDailyAvgTons(0);
      setProjectedTons(0);
      setTopSources([]);
      return;
    }

    let isCancelled = false;
    const controller = new AbortController();
    const fetchCurrent = async () => {
      setIsLoading(true);
      setError('');
      try {
        const query = selectedMonthISO ? `?month=${encodeURIComponent(selectedMonthISO)}` : '';
        const res = await fetch(`${API_URL}/api/projects/${projectId}/emissions/current-month${query}`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${user.token}`,
            'Content-Type': 'application/json'
          },
          signal: controller.signal
        });
        if (!res.ok) {
          let message = 'Failed to load current month emissions';
          try {
            const body = await res.json();
            message = body?.message || body?.error || message;
          } catch {}
          throw new Error(message);
        }
        const body = await res.json();
        const d = body?.data || {};
        if (!isCancelled) {
          setMonthLabel(d?.month || new Date().toLocaleString('en-US', { month: 'long' }));
          setTotalTons((Number(d?.totalEmissionsKg) || 0) / 1000);
          setChangePct(Number(d?.changeFromLastMonthPct) || 0);
          setIsIncrease(Boolean(d?.isIncrease));
          setTargetTons((Number(d?.targetKg) || 0) / 1000);
          setDailyAvgTons((Number(d?.dailyAverageKg) || 0) / 1000);
          setProjectedTons((Number(d?.projectedMonthEndKg) || 0) / 1000);
          setTopSources(Array.isArray(d?.topSources) ? d.topSources : []);
        }
      } catch (e) {
        if (!isCancelled) {
          setError(e?.message || 'Failed to load current month emissions');
          setMonthLabel('');
          setTotalTons(0);
          setChangePct(0);
          setIsIncrease(false);
          setTargetTons(0);
          setDailyAvgTons(0);
          setProjectedTons(0);
          setTopSources([]);
        }
      } finally {
        if (!isCancelled) setIsLoading(false);
      }
    };
    fetchCurrent();
    return () => {
      isCancelled = true;
      controller.abort();
    };
  }, [user?.token, selectedProject?.id, selectedMonthISO]);

  return (
    <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 h-full">
      <div className="flex items-center justify-between mb-6 relative">
        <h3 className="text-lg font-semibold text-gray-900">{t('dashboard.currentMonthWidget.title')}</h3>
        <button
          type="button"
          onClick={() => setIsPickerOpen((v) => !v)}
          className="p-1 rounded hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-600"
          aria-label="Select month"
          title="Select month"
        >
          <Calendar className="w-5 h-5 text-gray-500" />
        </button>

        {isPickerOpen && (
          <div
            ref={pickerRef}
            className="absolute right-0 top-full mt-2 z-20 bg-white border border-gray-200 rounded-lg shadow-lg p-3 w-64"
          >
            {(() => {
              const now = new Date();
              const nowIso = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
              const locale = i18n?.language || 'en-US';
              return (
                <>
            <div className="flex items-center justify-between mb-2">
              <button
                type="button"
                onClick={() => setPickerYear((y) => y - 1)}
                className="p-1 rounded hover:bg-gray-100"
                aria-label="Previous year"
              >
                <ChevronLeft className="w-4 h-4 text-gray-600" />
              </button>
              <span className="text-sm font-medium text-gray-900">{pickerYear}</span>
              <button
                type="button"
                onClick={() => setPickerYear((y) => (y >= now.getFullYear() ? y : y + 1))}
                className={`p-1 rounded ${pickerYear >= now.getFullYear() ? 'opacity-40 cursor-not-allowed' : 'hover:bg-gray-100'}`}
                aria-label="Next year"
              >
                <ChevronRight className="w-4 h-4 text-gray-600" />
              </button>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {Array.from({ length: 12 }).map((_, idx) => {
                const mm = String(idx + 1).padStart(2, '0');
                const iso = `${pickerYear}-${mm}`;
                const label = new Date(2000, idx, 1).toLocaleString(locale, { month: 'short' });
                const isSelected = iso === selectedMonthISO;
                const isFuture = iso > nowIso;
                const baseClasses = 'px-2 py-1 text-sm rounded border';
                const stateClasses = isSelected
                  ? 'bg-blue-50 border-blue-400 text-blue-700'
                  : isFuture
                    ? 'bg-gray-50 border-gray-200 text-gray-400 cursor-not-allowed'
                    : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50';
                return (
                  <button
                    key={mm}
                    type="button"
                    onClick={() => {
                      if (isFuture) return;
                      setSelectedMonthISO(iso);
                      setIsPickerOpen(false);
                    }}
                    aria-disabled={isFuture}
                    disabled={isFuture}
                    className={`${baseClasses} ${stateClasses}`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
                </>
              );
            })()}
          </div>
        )}
      </div>

      <div className="flex flex-col h-full space-y-6">
        {/* Current Month Emissions */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-600">{t('dashboard.currentMonthWidget.totalEmissions')}</span>
            <span className="text-sm font-medium text-gray-900">
              {displayMonthLabel} {currentYear}
            </span>
          </div>
          <div className="text-3xl font-bold text-gray-900">
            {isLoading ? (
              <span className="inline-block h-8 w-40 bg-gray-100 rounded animate-pulse" />
            ) : (
              <>
                {totalTons.toFixed(1)} tCO<sub>2</sub>e
              </>
            )}
          </div>
        </div>

        {/* Change from last month */}
        <div className="flex items-center space-x-2">
          <TrendingUp className={`w-4 h-4 ${isIncrease ? 'text-red-500' : 'text-green-500'}`} />
          <span className={`text-sm font-medium ${isIncrease ? 'text-red-600' : 'text-green-600'}`}>
            {isIncrease && changePct >= 0 ? '+' : ''}{changePct.toFixed(1)}%
          </span>
          <span className="text-sm text-gray-500">{t('dashboard.currentMonthWidget.changeFromLastMonth')}</span>
        </div>

        {/* Additional Metrics */}
        <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-100">
          <div>
            <div className="text-xs text-gray-500 mb-1">{t('dashboard.currentMonthWidget.dailyAverage')}</div>
            <div className="text-lg font-semibold text-gray-900">
              {isLoading ? (
                <span className="inline-block h-5 w-24 bg-gray-100 rounded animate-pulse" />
              ) : (
                <>
                  {dailyAvgTons.toFixed(1)} tCO<sub>2</sub>e
                </>
              )}
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-500 mb-1">{t('dashboard.currentMonthWidget.projected')}</div>
            <div className="text-lg font-semibold text-gray-900">
              {isLoading ? (
                <span className="inline-block h-5 w-24 bg-gray-100 rounded animate-pulse" />
              ) : (
                <>
                  {projectedTons.toFixed(0)} tCO<sub>2</sub>e
                </>
              )}
            </div>
          </div>
        </div>

        {/* Top Sources */}
        <div className="pt-4">
          <div className="text-xs text-gray-500 mb-2">{t('dashboard.currentMonthWidget.topSources')}</div>
          <div className="space-y-2">
            {isLoading ? (
              <>
                <div className="h-4 w-full bg-gray-100 rounded animate-pulse" />
                <div className="h-4 w-5/6 bg-gray-100 rounded animate-pulse" />
                <div className="h-4 w-2/3 bg-gray-100 rounded animate-pulse" />
              </>
            ) : (
              (topSources || []).slice(0, 5).map((s, idx) => (
                <div key={`${s?.sourceKey || idx}`} className="flex items-center justify-between">
                  <span className="text-sm text-gray-700">{s?.sourceKey || '-'}</span>
                  <span className="text-sm font-medium text-gray-900">{Math.round((Number(s?.percentage) || 0) * 100)}%</span>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Target Progress */}
        <div className="pt-6 border-t border-gray-100 mt-auto">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm text-gray-600">{t('dashboard.currentMonthWidget.monthlyTarget')}</span>
            <span className="text-sm font-medium text-gray-900">
              {isLoading ? (
                <span className="inline-block h-4 w-20 bg-gray-100 rounded animate-pulse" />
              ) : (
                <>
                  {targetTons.toFixed(0)} tCO<sub>2</sub>e
                </>
              )}
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3 mb-3">
            <div 
              className={`h-3 rounded-full ${isIncrease ? 'bg-yellow-500' : 'bg-green-500'}`}
              style={{ 
                width: `${Math.min(targetTons > 0 ? (totalTons / targetTons) * 100 : 0, 100)}%` 
              }}
            ></div>
          </div>
          <div className="flex items-center space-x-2">
            {isLoading ? (
              <div className="h-4 w-24 bg-gray-100 rounded animate-pulse" />
            ) : isIncrease ? (
              <div className="flex items-center text-yellow-600">
                <AlertCircle className="w-3 h-3 mr-1" />
                <span className="text-xs font-medium">{t('dashboard.currentMonthWidget.aboveTarget')}</span>
              </div>
            ) : (
              <div className="flex items-center text-green-600">
                <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                <span className="text-xs font-medium">{t('dashboard.currentMonthWidget.onTrack')}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default CurrentMonthWidget; 